import { FC, ReactNode } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Dialog, Stack } from '@mui/material';

import { MainButton } from '~components/atoms';
import { LocalizationTypography } from '~components/organisms/localization';

const dialogPaperSx = {
  '& .MuiPaper-root': {
    borderRadius: 3,
    width: '100%',
    maxWidth: 420,
    m: 2.5,
    p: 3,
  },
};

const BaseDialog: FC<{
  open: boolean;
  onClose: () => void;
  children: ReactNode;
}> = ({ open, onClose, children }) => (
  <Dialog open={open} onClose={onClose} sx={dialogPaperSx}>
    <Stack gap={2.5}>{children}</Stack>
  </Dialog>
);

const SuccessIcon: FC = () => (
  <Stack alignItems="center">
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{
        width: 96,
        height: 96,
        borderRadius: '50%',
        backgroundColor: 'brand.lightest',
      }}
    >
      <CheckCircleIcon sx={{ fontSize: 76, color: 'brand.main' }} />
    </Stack>
  </Stack>
);

interface CancelPassDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  passName: string;
  /**
   * End of the paid period — access runs to this date either way.
   *
   * Absent while a renewal is still being charged: no period has been paid for,
   * so there is no settled date to promise. The copy drops the clause rather
   * than quoting an empty one.
   */
  accessUntil?: string;
}

/**
 * Cancel interception. Deliberately leads with "Keep My Pass": cancelling ends
 * the recurring charge, and un-cancelling only works while the paid period is
 * still current.
 */
export const CancelPassDialog: FC<CancelPassDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  passName,
  accessUntil,
}) => (
  <BaseDialog open={open} onClose={onClose}>
    <LocalizationTypography
      textId="app.rentalPage.newStepThree.pass.cancelDialog.title"
      defaultText="Are you sure?"
      variant="Poppins/Heading-2"
    />
    {accessUntil ? (
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.cancelDialog.body"
        defaultText="If you cancel your {{passName}}, you will lose your charger swaps at the end of your current billing period ({{accessUntil}})."
        textValues={{ passName, accessUntil }}
        variant="Poppins/Body-md"
        color="#6B7280"
      />
    ) : (
      <LocalizationTypography
        textId="app.rentalPage.newStepThree.pass.cancelDialog.bodyNoDate"
        defaultText="If you cancel your {{passName}}, you will lose your charger swaps at the end of your current billing period."
        textValues={{ passName }}
        variant="Poppins/Body-md"
        color="#6B7280"
      />
    )}
    <Stack gap={1.5}>
      <MainButton variant="contained" color="primary" onClick={onClose}>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.cancelDialog.keep"
          defaultText="Keep My Pass"
          variant="Poppins/Subheading-md"
        />
      </MainButton>
      <MainButton
        variant="outlined"
        color="error"
        onClick={onConfirm}
        isLoading={isLoading}
        disabled={isLoading}
      >
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.cancelPass"
          defaultText="Cancel Pass"
          variant="Poppins/Subheading-md"
        />
      </MainButton>
    </Stack>
  </BaseDialog>
);

interface ResubscribeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  passName: string;
  /** Recurring price, e.g. "$9.99/month". */
  price: string;
}

export const ResubscribeDialog: FC<ResubscribeDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isLoading,
  passName,
  price,
}) => (
  <BaseDialog open={open} onClose={onClose}>
    <LocalizationTypography
      textId="app.rentalPage.newStepThree.pass.resubscribeDialog.title"
      defaultText="Resubscribe?"
      variant="Poppins/Heading-2"
    />
    <LocalizationTypography
      textId="app.rentalPage.newStepThree.pass.resubscribeDialog.body"
      defaultText="Your {{passName}} will be reactivated and you will be charged {{price}} on your next billing date."
      textValues={{
        passName,
        price,
        interpolation: { escapeValue: false },
      }}
      variant="Poppins/Body-md"
      color="#6B7280"
    />
    <Stack gap={1}>
      <MainButton
        variant="contained"
        color="primary"
        onClick={onConfirm}
        isLoading={isLoading}
        disabled={isLoading}
      >
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.resubscribe"
          defaultText="Resubscribe"
          variant="Poppins/Subheading-md"
        />
      </MainButton>
      <MainButton variant="text" color="inherit" onClick={onClose}>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.resubscribeDialog.dismiss"
          defaultText="Never Mind"
          variant="Poppins/Subheading-md"
          color="#6B7280"
        />
      </MainButton>
    </Stack>
  </BaseDialog>
);

interface PassResultDialogProps {
  open: boolean;
  onClose: () => void;
  passName: string;
  /** Only used by the cancellation variant. */
  accessUntil?: string;
  variant: 'cancelled' | 'reactivated';
}

/**
 * The confirmation shown after `CANCEL` or `RENEW` lands.
 */
export const PassResultDialog: FC<PassResultDialogProps> = ({
  open,
  onClose,
  passName,
  accessUntil,
  variant,
}) => {
  const isCancelled = variant === 'cancelled';

  // Three bodies, not two: a cancellation with no settled access date drops the
  // clause instead of quoting an empty one. Named here rather than nested in
  // the JSX, where a third axis turns the ternaries unreadable.
  const body = !isCancelled
    ? {
        textId: 'app.rentalPage.newStepThree.pass.reactivatedDialog.body',
        defaultText:
          'Your {{passName}} has been reactivated. You now have access to your charger swaps again.',
      }
    : accessUntil
    ? {
        textId: 'app.rentalPage.newStepThree.pass.cancelledDialog.body',
        defaultText:
          'Your {{passName}} has been cancelled. You will still have access until {{accessUntil}}.',
      }
    : {
        textId: 'app.rentalPage.newStepThree.pass.cancelledDialog.bodyNoDate',
        defaultText: 'Your {{passName}} has been cancelled.',
      };

  return (
    <BaseDialog open={open} onClose={onClose}>
      <SuccessIcon />
      <LocalizationTypography
        textId={
          isCancelled
            ? 'app.rentalPage.newStepThree.pass.cancelledDialog.title'
            : 'app.rentalPage.newStepThree.pass.reactivatedDialog.title'
        }
        defaultText={isCancelled ? 'Pass Cancelled' : 'Pass Reactivated'}
        variant="Poppins/Heading-2"
        textAlign="center"
      />
      <LocalizationTypography
        textId={body.textId}
        defaultText={body.defaultText}
        textValues={{ passName, accessUntil }}
        variant="Poppins/Body-md"
        color="#6B7280"
        textAlign="center"
      />
      <MainButton variant="contained" color="primary" onClick={onClose}>
        <LocalizationTypography
          textId="app.rentalPage.newStepThree.pass.backToRental"
          defaultText="Back to Rental"
          variant="Poppins/Subheading-md"
        />
      </MainButton>
    </BaseDialog>
  );
};
